import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { useContext, useEffect } from "react";
import { UserTypeContext } from "../contexts/UserTypeContext";
import enumStrings, { RoutePaths } from "../enumStrings";
import { UserType } from "../graphql/types";
import { hasPendingNewPasswordChallenge } from "../components/pages/Auth/pendingNewPasswordChallenge";
import { getSingleDashboardUserType } from "../components/pages/Auth/dashboardUserType";
import { connectionStatus } from "../utils/connectionStatus";

export const getLoggedOutRedirectPath = ({
  newPasswordChallengeIsPending,
  urlPath,
}: {
  newPasswordChallengeIsPending: boolean;
  urlPath: string;
}) => {
  if (urlPath === RoutePaths.secureCheckoutReturn) {
    return null;
  }

  if (newPasswordChallengeIsPending) {
    return urlPath === RoutePaths.newPassword ? null : RoutePaths.newPassword;
  }

  if (
    urlPath === RoutePaths.login || urlPath === RoutePaths.forgotPassword
  ) {
    return null;
  }

  return RoutePaths.login;
};

const useInitialAuth = (setIsLoggedIn: (val: any) => void) => {
  const {
    setters: { setUserType },
  } = useContext(UserTypeContext);
  return useEffect(() => {
    const checkAccessToken = async (): Promise<void> => {
      // Remove plaintext credentials left by any legacy build regardless of
      // whether the current access token is still valid. New password
      // challenges are held only in memory.
      sessionStorage.removeItem(
        enumStrings.sessionStorageKey.loggedInWithTemporaryPassword
      );

      let accessTokenIsValid = false;
      let userType = null;
      try {
        const res = await fetchAuthSession();

        if (res?.tokens?.accessToken?.payload) {
          userType = getSingleDashboardUserType(
            res.tokens.accessToken.payload as Record<string, unknown>
          );

          if (
            userType === UserType.ChamberAdmin ||
            userType === UserType.ChamberStaff
            //  ||
            // userType === UserType.ChamberMemberAdmin
          ) {
            accessTokenIsValid = true;
          }
        }
      } catch (error) {
        if (error !== "No current user") {
          // A token refresh with no internet fails here. The check shows
          // the offline screen instead of a loader that never ends.
          void connectionStatus.checkConnection();
          throw error;
        }
      }

      const urlPath = window.location.pathname;
      if (!accessTokenIsValid) {
        setIsLoggedIn(false);
        setUserType(null);

        const newPasswordChallengeIsPending =
          hasPendingNewPasswordChallenge();

        const redirectPath = getLoggedOutRedirectPath({
          newPasswordChallengeIsPending,
          urlPath,
        });

        if (newPasswordChallengeIsPending) {
          if (redirectPath) {
            window.location.href = redirectPath;
          }
          return;
        }

        await signOut();

        // user isn't logged in and isn't trying to access the cpa delete account page or isn't setting their permanent password... so force them to the login page
        if (redirectPath) {
          window.location.href = redirectPath;
        }
      } else {
        setUserType(userType);
        setIsLoggedIn(true);

        if (urlPath === RoutePaths.login) {
          window.location.href = RoutePaths.home;
          return;
        }
      }
    };
    checkAccessToken();
  }, [setIsLoggedIn, setUserType]);
};

export default useInitialAuth;
